import { create } from "zustand";
import { persist } from "zustand/middleware";
import Cookies from "universal-cookie";
import { encryptedStorage } from "@/utils/encryptedStorage";

const cookies = new Cookies();

// Keys that must never be overwritten via setStates/setState from external callers.
const ACTION_KEYS = new Set([
    "setLogin",
    "logout",
    "getStates",
    "setStates",
    "getState",
    "setState",
    "setStateSmart",
    "deleteState",
    "setCookie",
    "getCookie",
    "deleteCookie",
]);

/* ----------------------------------------
   Helpers
---------------------------------------- */

const safeJSONParse = (value) => {
    try {
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

const getCookieJSON = (key) => safeJSONParse(cookies.get(key));

const mergeByKey = (oldList = [], newList = [], key) => {
    if (!Array.isArray(newList)) return oldList;

    if (!key) return [...oldList, ...newList];

    const map = new Map();
    oldList.forEach(item => {
        if (item?.[key] !== undefined) map.set(item[key], item);
    });

    let changed = false;
    const sizeBeforeNew = map.size;
    newList.forEach(item => {
        if (item?.[key] !== undefined) {
            if (!changed && (!map.has(item[key]) || map.get(item[key]) !== item)) changed = true;
            map.set(item[key], item);
        }
    });

    if (!changed && map.size === sizeBeforeNew) return oldList;
    return Array.from(map.values());
};

/* ----------------------------------------
   Initial Data
---------------------------------------- */

const loginDataFromCookie = getCookieJSON("loginData");

/* ----------------------------------------
   Store
---------------------------------------- */

export const useGlobalStore = create(
    persist(
        (set, get) => ({
            loginData: loginDataFromCookie || null,
            theme: "light",
            states: {},

            /* ---------- Auth ---------- */
            setLogin: (payload) => {
                set({ loginData: payload });
                cookies.set("loginData", JSON.stringify(payload), { path: "/" });
            },
            logout: () => {
                cookies.remove("loginData", { path: "/" });
                set({ loginData: null, states: {} });
            },

            /* ---------- Global States ---------- */
            getStates: () => ({ ...get() }),
            setStates: (payload = {}) =>
                set((s) => {
                    const filtered = Object.fromEntries(
                        Object.entries(payload || {}).filter(([k]) => !ACTION_KEYS.has(k))
                    );
                    const keys = Object.keys(filtered);
                    if (!keys.length) return s;
                    if (!keys.some((key) => !Object.is(s[key], filtered[key]))) return s;
                    return { ...s, ...filtered };
                }),

            getState: (key) => get()[key],
            setState: (key, value) =>
                set((s) => {
                    if (ACTION_KEYS.has(key) || Object.is(s[key], value)) return s;
                    return { ...s, [key]: value };
                }),
            setStateSmart: (key, value, uniqueKey = null) =>
                set((s) => {
                    if (ACTION_KEYS.has(key)) return s;
                    const merged = mergeByKey(s[key], value, uniqueKey);
                    if (Object.is(merged, s[key])) return s;
                    return { ...s, [key]: merged };
                }),
            deleteState: (key) =>
                set((s) => {
                    if (ACTION_KEYS.has(key)) return s;
                    const { [key]: _, ...rest } = s;
                    return rest;
                }),

            /* ---------- Cookies ---------- */

            setCookie: (key, value) => cookies.set(key, JSON.stringify(value), { path: "/" }),
            getCookie: getCookieJSON,
            deleteCookie: (key) => cookies.remove(key, { path: "/" })
        }),
        {
            name: "global-store",
            storage: encryptedStorage,
            partialize: (state) => ({
                loginData: state.loginData,
                // ipAddress persisted so the IP-lookup query is skipped on return visits
                ipAddress: state.ipAddress,
                // `states` excluded — it holds API responses re-fetched on demand anyway;
                // persisting it adds unnecessary encrypt/decrypt overhead on startup
            })
        }
    )
);

export default useGlobalStore;
